import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { OilIcon } from './OilIcons';
import { music, playClip } from './oilSounds';

const CLIP = {
  extract: '/oil/video/extract_pumpjack.webm',
  bottle: '/oil/video/bottling_line.webm',
};
const SFX_FLASH = '/oil/sfx_flash.mp3';
const SFX_DROPS = '/oil/sfx_gotas.mp3';

// Card quadrado com o clipe da operação; o flash branco do fim do vídeo
// continua no card e o resultado nasce do branco até a cor real.
export const OilProcessVideo = ({ kind, result, barrelLevel, onReveal, onClose }) => {
  const [phase, setPhase] = useState('video'); // video | flash | result
  const [washed, setWashed] = useState(true); // resultado ainda estourado de branco
  const vidRef = useRef(null);
  const ended = useRef(false);
  const done = useRef(false);
  const flashed = useRef(false);

  useEffect(() => {
    music.duck(true);
    return () => music.duck(false);
  }, []);

  useEffect(() => {
    const v = vidRef.current;
    if (phase !== 'video' || !v) return;
    const skip = () => { ended.current = true; reveal(); };
    // o clarão do clipe: som entra 0,5s antes do fim
    const onTime = () => {
      if (flashed.current || !v.duration) return;
      if (v.duration - v.currentTime <= 0.5) { flashed.current = true; playClip(SFX_FLASH); }
    };
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('error', skip);
    v.play().catch(() => { v.muted = true; v.play().catch(skip); });
    const t = setTimeout(skip, 9000);
    return () => {
      clearTimeout(t);
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('error', skip);
    };
  }, [phase]);

  const reveal = () => {
    if (done.current || !ended.current || !result) return;
    done.current = true;
    if (!flashed.current) { flashed.current = true; playClip(SFX_FLASH); }
    setPhase('flash');
    setTimeout(() => {
      setPhase('result');
      onReveal?.(result);
      // do branco estourado até a cor real do card
      requestAnimationFrame(() => requestAnimationFrame(() => setWashed(false)));
    }, 260);
  };

  // o resultado pode chegar do servidor antes ou depois do fim do clipe
  useEffect(() => { reveal(); }, [result]);

  const collect = () => {
    if (kind === 'extract' && result?.drops > 0) playClip(SFX_DROPS);
    onClose();
  };

  const dry = kind === 'extract' && result?.drops === 0;
  const units = Math.max(1, Number(result?.units) || 1);

  return createPortal(
    <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/90 p-5 backdrop-blur-sm"
      data-testid="oil-process-overlay">
      <div data-testid="oil-process-card"
        className="relative aspect-square w-[86vw] max-w-sm overflow-hidden rounded-2xl border-2 border-amber-500/70 bg-black shadow-[0_0_50px_rgba(245,158,11,0.35)]">
        <video ref={vidRef} playsInline preload="auto" data-testid="oil-process-video"
          onEnded={() => { ended.current = true; reveal(); }}
          className="absolute inset-0 h-full w-full object-cover">
          <source src={CLIP[kind]} type="video/webm" />
        </video>

        {phase === 'result' && (
          <div data-testid="oil-process-result"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-b from-black/70 via-black/85 to-black/95 p-6 text-center"
            style={{
              opacity: washed ? 0.25 : 1,
              transform: `scale(${washed ? 0.72 : 1})`,
              filter: washed ? 'brightness(6) saturate(0) blur(3px)' : 'none',
              transition: 'opacity 620ms ease-out, transform 620ms cubic-bezier(.16,1,.3,1), filter 720ms ease-out',
            }}>
            {kind === 'extract' ? (
              dry ? (
                <>
                  <span className="text-5xl">🕳️</span>
                  <p className="text-lg font-black uppercase tracking-widest text-zinc-300">
                    {units > 1 ? `${units} poços secos` : 'Poço seco'}
                  </p>
                  <p className="max-w-[80%] text-xs text-zinc-400">O cavalo bombeou areia. Manda de novo que a jazida vira.</p>
                </>
              ) : (
                <>
                  <OilIcon k="drops" className="!h-16 drop-shadow-[0_0_18px_rgba(56,189,248,0.55)]" />
                  <p className="text-4xl font-black text-sky-300 drop-shadow-[0_0_14px_rgba(56,189,248,0.6)]"
                    data-testid="oil-process-drops">+{result?.drops}</p>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-200/80">Gotas de petróleo</p>
                  {units > 1 && (
                    <p className="text-[10px] font-bold text-zinc-400" data-testid="oil-process-batch">
                      {units} extrações{result?.dry ? ` · ${result.dry} ${result.dry > 1 ? 'poços secos' : 'poço seco'}` : ''}
                    </p>
                  )}
                </>
              )
            ) : (
              <>
                <div className="relative">
                  <img src={`/oil/barril_lv${barrelLevel || 1}.webp`} alt=""
                    className="h-24 w-auto drop-shadow-[0_0_18px_rgba(245,158,11,0.55)]" />
                  {units > 1 && (
                    <span className="absolute -right-2 -top-1 rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-black text-black shadow-lg"
                      data-testid="oil-process-batch">×{units}</span>
                  )}
                </div>
                <p className="text-2xl font-black uppercase tracking-widest text-amber-300 drop-shadow-[0_0_14px_rgba(245,158,11,0.6)]"
                  data-testid="oil-process-barrel">
                  {units > 1 ? `${units} barris LV${barrelLevel}` : `Barril LV${barrelLevel}`}
                </p>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-200/80">
                  {result?.drops ? `Consumiu ${result.drops} gotas` : 'Envasado e lacrado'}
                </p>
              </>
            )}
            <button onClick={collect} data-testid="oil-process-close"
              className="mt-2 rounded-full border border-amber-600/70 bg-black/70 px-6 py-2 text-[10px] font-black uppercase tracking-widest text-amber-300 transition-transform active:scale-95">
              {kind === 'extract' && !dry ? 'Coletar' : 'Continuar'}
            </button>
          </div>
        )}

        {/* clarão branco: fecha o clipe e entrega o card */}
        <div data-testid="oil-process-flash"
          className="pointer-events-none absolute inset-0 bg-white"
          style={{
            opacity: phase === 'video' ? 0 : phase === 'flash' ? 1 : 0,
            transition: phase === 'flash' ? 'opacity 120ms linear' : 'opacity 620ms ease-out',
          }} />
      </div>
    </div>,
    document.body
  );
};

export default OilProcessVideo;
